import { ApolloClient, isApolloError } from '@apollo/client';
import { omit } from 'lodash';
import * as moment from 'moment-timezone';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import gql from 'graphql-tag';
import { setDatadogUser } from 'App/Datadog/DatadogSetup';
import type { LDClientBase } from 'launchdarkly-js-sdk-common';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { getExperimentAbTests } from 'flags/experiments';
import { logException } from '../logging';
import { Errors } from '../../Shared/Errors';
import { getUserLocale } from '../../i18n/getUserLocale';
import tracking from './tracking';
import { IdentifyUserForTracking } from './__generated__/IdentifyUserForTracking';

export const IDENTIFY_USER_FOR_TRACKING_QUERY = gql`
  query IdentifyUserForTracking {
    user {
      activationDate
      createdAt: created
      id
      firstName
      lastName
      locationData {
        id
        name
      }
      departmentData {
        id
        name
      }
      email
      timezone
      userType {
        id
        name
      }
      permissions {
        value
      }
      praiseDirectory {
        value
      }
      group {
        clientTier
        groupId
        groupName
        subdomain: subDomain
      }
      isLineManager
      onboardingStatus {
        value
      }
    }
  }
`;

export async function trackLoggedInUser(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: ApolloClient<any>,
  ldClient?: LDClientBase,
) {
  const [{ data }, webAbTests] = await Promise.all([
    client.query<IdentifyUserForTracking>({
      query: IDENTIFY_USER_FOR_TRACKING_QUERY,
    }),
    getExperimentAbTests(ldClient),
  ]);

  if (data.user) {
    const {
      group,
      praiseDirectory,
      userType,
      permissions,
      departmentData,
      locationData,
      id: uuid,
      isLineManager,
      onboardingStatus,
      ...user
    } = data.user;

    const locale = getUserLocale();

    const timezone = user.timezone ? user.timezone : moment.tz.guess();

    const traits = {
      ...omit(user, '__typename'),
      firstName: user.firstName || undefined,
      lastName: user.lastName || undefined,
      email: user.email || undefined,
      id: uuid,
      uuid,
      timezone,
      subdomain: group?.subdomain ?? '',
      groupId: group?.groupId ?? '',
      praiseDirectoryEnabled: Boolean(Number(praiseDirectory?.value)),
      client: group?.groupName || undefined,
      clientTier: group?.clientTier || undefined,
      userType: userType?.name ?? '',
      department: departmentData?.name || undefined,
      departmentId: departmentData?.id || undefined,
      location: locationData?.name || undefined,
      locationId: locationData?.id || undefined,
      accessType: permissions?.value?.toLowerCase() ?? 'user',
      // Reported separately from the `abTests` trait that mobile reports, so
      // that neither platform overwrites the other's experiments.
      //
      // Omitted entirely when the experiments could not be determined, so an
      // intermittent LaunchDarkly failure leaves the existing value in place
      // rather than clearing it
      ...(webAbTests && { webAbTests }),
      locale: locale || '',
      identifiedAsLineManager: Boolean(isLineManager),
      onboardingStatus: onboardingStatus?.value ?? undefined,
    };

    tracking.identifyUser({
      userId: uuid,
      traits,
    });

    if (window.clarity) {
      window.clarity('identify', uuid, {});
    }

    setDatadogUser({
      id: uuid,
      subDomain: traits.subdomain,
      isLineManager: traits.identifiedAsLineManager,
      organisationId: traits.groupId,
      locale: traits.locale,
      timezone,
      userType: traits.userType,
      role: traits.accessType,
      departmentName: traits.department,
      locationName: traits.location,
    });
  }
}

const ignoredErrors = [
  Errors.UnauthorizedError,
  Errors.TokenExpiredError,
  Errors.InternalServerError,
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useTrackLoggedInUser = (client: ApolloClient<any> | undefined) => {
  const location = useLocation();
  const ldClient = useLDClient();

  useEffect(() => {
    if (client) {
      trackLoggedInUser(client, ldClient).catch(e => {
        if (isApolloError(e)) {
          if (e.graphQLErrors.length) {
            const [{ name }] = e.graphQLErrors;

            if (!ignoredErrors.includes(name as Errors)) {
              logException(e);
            }
          }
        } else {
          logException(e);
        }
      });
    }
  }, [client, location, ldClient]);
};

export default useTrackLoggedInUser;
